import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthLayout from '../../layouts/auth/authLayout';
import authService from '../../services/authService';
import '../../styles/others/ForgotPassword.css';

import eyeOpen from '../../assets/icons/indicators/eye_open_icon.svg';
import eyeClosed from '../../assets/icons/indicators/eye_closed_icon.svg'; 

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [tenantId, setTenantId] = useState(null);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Get tenant information on component mount
  useEffect(() => {
    const tenant = authService.getCurrentTenant();
    if (tenant) {
      setTenantId(tenant._id);
      console.log('Tenant found for password reset:', tenant.name);
    } else {
      console.log('No tenant found, using default database');
    }
  }, []);

  const handleSendCode = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Pass tenantId to forgotPassword
      await authService.forgotPassword(email, tenantId);
      setIsCodeSent(true);
      toast.success('Verification code sent to your email');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to send verification code';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!verificationCode) {
      setError('Verification code is required');
      return;
    }
    
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // First verify the code with tenantId
      await authService.verifyResetCode(email, verificationCode, tenantId);
      
      // Then reset the password with tenantId
      await authService.resetPassword(email, verificationCode, newPassword, confirmPassword, tenantId);
      
      toast.success('Password reset successfully');
      navigate('/login');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to reset password';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      // Pass tenantId to forgotPassword
      await authService.forgotPassword(email, tenantId);
      toast.success('Verification code resent to your email');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to resend verification code';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setIsCodeSent(false);
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  return (
    <AuthLayout>
      {!isCodeSent ? (
        <form onSubmit={handleSendCode} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder="Enter your email address"
              className={error ? 'error' : ''}
              disabled={isLoading}
            />
            {error && <div className="error-message">{error}</div>}
          </div>
          
          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Verification Code'}
          </button>
          
          <div className="auth-footer">
            <p>Remember your password? <Link to="/login">Sign In</Link></p>
          </div>
        </form>
      ) : (
        <form onSubmit={handleResetPassword} className="auth-form">
          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                setError('');
              }}
              placeholder="Enter verification code"
              className={error ? 'error' : ''}
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError('');
                }}
                placeholder="Enter new password"
                className={error ? 'error' : ''}
                disabled={isLoading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex="-1"
              >
                <img 
                  src={showNewPassword ? eyeOpen : eyeClosed} 
                  alt={showNewPassword ? "Hide password" : "Show password"}
                />
              </button>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                placeholder="Confirm new password"
                className={error ? 'error' : ''}
                disabled={isLoading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex="-1"
              >
                <img 
                  src={showConfirmPassword ? eyeOpen : eyeClosed} 
                  alt={showConfirmPassword ? "Hide password" : "Show password"}
                />
              </button>
            </div>
            {error && <div className="error-message">{error}</div>}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
          
          <div className="auth-actions">
            <button 
              type="button" 
              className="forgot-pass-btn-link" 
              onClick={handleResendCode}
              disabled={isLoading}
            >
              Resend Code
            </button>
          </div>
          
          <div className="auth-footer">
            <p>Remember your password? <Link to="/login">Sign In</Link></p>
          </div>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;